import React, { useState, useEffect } from 'react'
import { RefreshCw } from 'lucide-react'

const baseUrl = import.meta.env.VITE_API_URL || 'https://orbglide-analyzer.orbglide.workers.dev'

async function fetchLeaders(category = 'all', limit = 10) {
  const res = await fetch(`${baseUrl}/api/nfl/leaders?category=${category}&limit=${limit}`)
  if (!res.ok) throw new Error('Failed to fetch leaders')
  return res.json()
}

// Category chip labels - shorter for cleaner UI
const CATEGORIES = [
  { key: 'passing_yards', label: 'PASS' },
  { key: 'rushing_yards', label: 'RUSH' },
  { key: 'receiving_yards', label: 'REC' },
  { key: 'receptions', label: 'RECS' },
  { key: 'touchdowns', label: 'TD' },
]

// Position abbreviations
const POS_ABBREV = {
  'Quarterback': 'QB',
  'Running Back': 'RB',
  'Wide Receiver': 'WR',
  'Tight End': 'TE',
  'Fullback': 'FB',
}

// Compact leader row - matches site styling (soft grays, proper spacing)
function LeaderRow({ player, rank, onClick }) {
  const pos = POS_ABBREV[player.position] || player.position?.slice(0, 2) || ''
  
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
    >
      {/* Rank */}
      <span className="w-5 text-[11px] font-semibold text-gray-400 text-center shrink-0">
        {rank}
      </span>
      
      {/* Player + Team inline */}
      <div className="flex-1 min-w-0 flex items-center gap-1.5">
        <span className="text-[12px] font-medium text-gray-600 truncate">{player.playerName}</span>
        <span className="text-[10px] text-gray-400 shrink-0">{player.team}</span>
        {pos && (
          <span className="text-[9px] font-medium text-gray-400 shrink-0">
            {pos}
          </span>
        )}
      </div>
      
      {/* Value + per game */}
      <div className="text-right shrink-0 flex items-baseline gap-1">
        <span className="text-[12px] font-semibold text-gray-600">{player.value?.toLocaleString()}</span>
        <span className="text-[10px] text-gray-400">{player.perGame}/g</span>
      </div>
    </button>
  )
}

function LeadersSection({ leaders, selectedCategory, onCategoryChange, onAskAI }) {
  const categories = Object.keys(leaders || {})
  
  if (categories.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm">
        No leaders data available
      </div>
    )
  }
  
  const currentCategory = (selectedCategory && categories.includes(selectedCategory)) ? selectedCategory : categories[0]
  const currentLeaders = leaders[currentCategory] || []
  
  return (
    <div className="space-y-3">
      {/* Category chip rail - sleek and compact */}
      <div className="flex justify-center gap-1.5 pb-1" style={{ overflow: 'hidden' }}>
        {CATEGORIES.filter(c => categories.includes(c.key)).map(cat => {
          const isActive = cat.key === currentCategory
          return (
            <button
              key={cat.key}
              onClick={() => onCategoryChange(cat.key)}
              className={`shrink-0 px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${
                isActive 
                  ? 'bg-gray-800 text-white' 
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-150'
              }`}
            >
              {cat.label}
            </button>
          )
        })}
      </div>
      
      {/* Leaders list - clean card style */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center px-3 py-2 bg-gray-50 border-b border-gray-200 text-[9px] font-semibold text-gray-400 uppercase tracking-wide">
          <span className="w-5 text-center">#</span>
          <span className="flex-1 ml-2">Player</span>
          <span className="text-right">Total</span>
        </div>
        {currentLeaders.slice(0, 10).map((player, idx) => (
          <LeaderRow
            key={player.playerId}
            player={player}
            rank={idx + 1}
            onClick={() => onAskAI?.({ 
              question: `Tell me about ${player.playerName}'s season`, 
              title: player.playerName 
            })}
          />
        ))}
      </div>
    </div>
  )
}

export default function LeadersTab({ league = 'nfl', onAskAI }) {
  const [leaders, setLeaders] = useState({})
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [emptyMessage, setEmptyMessage] = useState(null)

  const loadData = async () => {
    try {
      if (league !== 'nfl') {
        setLeaders({})
        setEmptyMessage('Leaders available for NFL only')
        setError(null)
        return
      }

      const leadersRes = await fetchLeaders('all', 15)
      
      if (leadersRes?.error) {
        setLeaders({})
        setEmptyMessage(leadersRes.message || 'Leaders data not available yet.')
        setError(null)
        return
      }

      const categories = leadersRes.categories || {}
      const categoryKeys = Object.keys(categories)

      setLeaders(categories)
      if (!selectedCategory || !categoryKeys.includes(selectedCategory)) {
        setSelectedCategory(categoryKeys[0] || null)
      }
      setEmptyMessage(categoryKeys.length === 0 ? 'No leaders data available yet.' : null)
      setError(null)
    } catch (err) {
      setError(err.message)
    }
  }

  useEffect(() => {
    setIsLoading(true)
    loadData().finally(() => setIsLoading(false))
  }, [league])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-5 h-5 text-gray-300 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-3 py-8 text-center">
        <p className="text-sm text-gray-400">{error}</p>
        <button onClick={loadData} className="mt-2 text-xs text-pink-500 hover:text-pink-600">
          Try again
        </button>
      </div>
    )
  }

  if (emptyMessage) {
    return (
      <div className="px-3 py-8 text-center">
        <p className="text-sm text-gray-400">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="px-3 py-2">
      <LeadersSection 
        leaders={leaders} 
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        onAskAI={onAskAI} 
      />
    </div>
  )
}
